<template>
  <div class="col-sm-12 delivery-history-page">
    <div class="history-container">
      <!-- =====================================================
           FILTER SECTION
      ====================================================== -->
      <div class="section-card filter-section">
        <div class="section-header">
          <div>
            <h6>Delivery Schedule History</h6>

            <span>
              Filter saved delivery schedules by distributor, route and dealer.
            </span>
          </div>

          <div class="section-count">
            {{ filteredHistoryList.length }}
            {{ filteredHistoryList.length === 1 ? "record" : "records" }}
          </div>
        </div>

        <div class="filter-grid">
          <!-- Distributor -->
          <div class="form-item">
            <label>Distributor</label>

            <select
              v-model="selectedDistributorId"
              :disabled="isDistributorLocked || loading || loadingDistributors"
              @change="onDistributorChange"
            >
              <option value="">All Distributors</option>

              <option
                v-for="distributor in distributorList"
                :key="distributor.profile_id"
                :value="String(distributor.profile_id)"
              >
                {{ distributor.name }}
              </option>
            </select>
          </div>

          <!-- Route -->
          <div class="form-item">
            <label>Route</label>

            <select
              v-model="selectedRouteId"
              :disabled="
                loading ||
                loadingRoutes ||
                !selectedDistributorId ||
                !availableRouteList.length
              "
              @change="onRouteChange"
            >
              <option value="">
                {{
                  selectedDistributorId
                    ? loadingRoutes
                      ? "Loading routes..."
                      : "All Routes"
                    : "Select distributor first"
                }}
              </option>

              <option
                v-for="route in availableRouteList"
                :key="route.id"
                :value="String(route.id)"
              >
                {{ route.name }}
              </option>
            </select>
          </div>

          <!-- Dealer -->
          <div class="form-item">
            <label>Dealer</label>

            <select
              v-model="selectedDealerId"
              :disabled="
                loading ||
                loadingDealers ||
                !selectedRouteId ||
                !availableDealerList.length
              "
              @change="onDealerChange"
            >
              <option value="">
                {{
                  selectedRouteId
                    ? loadingDealers
                      ? "Loading dealers..."
                      : availableDealerList.length
                      ? "All Dealers"
                      : "No dealers found"
                    : "Select route first"
                }}
              </option>

              <option
                v-for="dealer in availableDealerList"
                :key="dealer.id"
                :value="String(dealer.id)"
              >
                {{ dealer.name }}
              </option>
            </select>
          </div>

          <!-- Search -->
          <div class="form-item button-item">
            <label class="hidden-label">Search</label>

            <button
              type="button"
              class="btn button-default search-button"
              :disabled="
                loading ||
                loadingDistributors ||
                loadingRoutes ||
                loadingDealers
              "
              @click="searchHistory"
            >
              <span
                v-if="loading"
                class="spinner-border spinner-border-sm"
              ></span>

              <span v-else>Search</span>
            </button>
          </div>

          <!-- Reset -->
          <div class="form-item button-item reset-item">
            <label class="hidden-label">Reset</label>

            <button
              type="button"
              class="btn reset-button"
              :disabled="
                loading ||
                loadingDistributors ||
                loadingRoutes ||
                loadingDealers
              "
              @click="resetFilters"
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      <!-- =====================================================
           TABLE SECTION
      ====================================================== -->
      <div class="section-card table-section">
        <div class="section-header table-header">
          <div>
            <h6>Schedule History</h6>

            <span>
              Review previously created delivery schedules and edit when needed.
            </span>
          </div>

          <div class="table-summary-pill">
            {{ filteredHistoryList.length }}
            {{ filteredHistoryList.length === 1 ? "schedule" : "schedules" }}
          </div>
        </div>

        <div v-if="loading" class="loading-card">
          <span class="spinner-border spinner-border-sm"></span>
          Loading delivery schedules...
        </div>

        <div v-else class="history-table-card">
          <div class="history-table-scroll">
            <table class="history-table">
              <thead>
                <tr>
                  <th>Delivery Schedule Id</th>
                  <th>Distributor Name</th>
                  <th>Route</th>
                  <th>Schedule</th>
                  <th>Schedule Created Date</th>
                  <th>Status</th>
                  <th class="action-column">Action</th>
                </tr>
              </thead>

              <tbody>
                <tr v-for="schedule in filteredHistoryList" :key="schedule.id">
                  <td>
                    <span class="schedule-id-pill">
                      {{ schedule.schedule_display_id }}
                    </span>
                  </td>

                  <td class="primary-cell">
                    {{ schedule.distributor_name }}
                  </td>

                  <td>
                    <span class="route-pill">
                      {{ schedule.route_name }}
                    </span>
                  </td>

                  <td>
                    <div class="schedule-cell">
                      <strong>{{ schedule.schedule_type_label }}</strong>
                      <span>{{ schedule.schedule_text }}</span>
                      <small>{{ schedule.time_slot_label }}</small>
                    </div>
                  </td>

                  <td>
                    <div class="date-cell">
                      <strong>{{ formatDate(schedule.created_at) }}</strong>
                      <span>{{ formatTime(schedule.created_at) }}</span>
                    </div>
                  </td>

                  <td>
                    <span
                      class="status-pill"
                      :class="getStatusClass(schedule.status)"
                    >
                      {{ getStatusLabel(schedule.status) }}
                    </span>
                  </td>

                  <td class="action-column">
                    <button
                      type="button"
                      class="edit-button"
                      @click="editSchedule(schedule)"
                    >
                      <i class="fa fa-pencil"></i>
                      Edit
                    </button>
                  </td>
                </tr>

                <tr v-if="!filteredHistoryList.length">
                  <td colspan="7" class="empty-table">
                    No delivery schedule history found for selected filters.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useToast } from "vue-toastification";

import {
  fetchDistributors as fetchDistributorsApi,
  fetchRouteList,
  fetchDealersByRoutesHistory,
  fetchDeliverySchedules,
} from "@/services/dataService";

export default {
  name: "DeliveryScheduleHistoryData",

  data() {
    const currentDate = new Date().toISOString().split("T")[0];

    return {
      currentDate,

      loading: false,
      loadingDistributors: false,
      loadingRoutes: false,
      loadingDealers: false,
      searchedOnce: false,

      token: localStorage.getItem("token_set"),
      profileId: localStorage.getItem("profile_id"),
      userRole: localStorage.getItem("user_label"),

      isDistributorLocked: false,

      selectedDistributorId: "",
      selectedRouteId: "",
      selectedDealerId: "",

      appliedFilters: {
        distributor_id: "",
        route_id: "",
        route_name: "",
        dealer_id: "",
        dealer_name: "",
      },

      distributorList: [],
      routeList: [],
      dealerList: [],
      historyList: [],
    };
  },

  setup() {
    const toast = useToast();

    return {
      toast,
    };
  },

  computed: {
    isDistributorUser() {
      const user = String(this.userRole || "")
        .toUpperCase()
        .trim();

      return (
        user === "DISTRIBUTOR" ||
        user.startsWith("DISTRIBUTOR") ||
        user === "DIS" ||
        user.startsWith("DIS_")
      );
    },

    availableRouteList() {
      return this.routeList;
    },

    availableDealerList() {
      return this.dealerList;
    },

    historyHasDealerData() {
      return this.historyList.some((schedule) => {
        return (
          (Array.isArray(schedule.dealer_ids) && schedule.dealer_ids.length) ||
          (Array.isArray(schedule.dealer_names) && schedule.dealer_names.length)
        );
      });
    },

    filteredHistoryList() {
      return this.historyList.filter((schedule) => {
        const distributorMatched =
          !this.appliedFilters.distributor_id ||
          String(schedule.distributor_id) ===
            String(this.appliedFilters.distributor_id);

        const routeMatched =
          !this.appliedFilters.route_id ||
          this.scheduleHasRoute(
            schedule,
            this.appliedFilters.route_id,
            this.appliedFilters.route_name,
          );

        let dealerMatched = true;

        if (this.appliedFilters.dealer_id && this.historyHasDealerData) {
          dealerMatched = this.scheduleHasDealer(
            schedule,
            this.appliedFilters.dealer_id,
            this.appliedFilters.dealer_name,
          );
        }

        return distributorMatched && routeMatched && dealerMatched;
      });
    },
  },

  async mounted() {
    await this.loadInitialData();
  },

  methods: {
    async loadInitialData() {
      await Promise.all([
        this.fetchDistributors(),
        this.loadDeliveryScheduleHistory(false),
      ]);

      if (this.isDistributorLocked && this.selectedDistributorId) {
        this.appliedFilters.distributor_id = this.selectedDistributorId;
      }
    },

    bodyOf(response) {
      return response && response.data ? response.data : response;
    },

    listOf(body) {
      if (Array.isArray(body)) {
        return body;
      }

      if (body && Array.isArray(body.data)) {
        return body.data;
      }

      if (body && Array.isArray(body.Data)) {
        return body.Data;
      }

      if (body && Array.isArray(body.result)) {
        return body.result;
      }

      if (body && Array.isArray(body.Result)) {
        return body.Result;
      }

      if (body && body.data && Array.isArray(body.data.data)) {
        return body.data.data;
      }

      if (body && body.Data && Array.isArray(body.Data.Data)) {
        return body.Data.Data;
      }

      return [];
    },

    namedListOf(body, keys) {
      const containers = [
        body,
        body?.data,
        body?.Data,
        body?.result,
        body?.Result,
      ];

      for (const container of containers) {
        if (!container || Array.isArray(container)) {
          continue;
        }

        for (const key of keys) {
          if (Array.isArray(container[key])) {
            return container[key];
          }
        }
      }

      return [];
    },

    isSuccessResponse(body) {
      if (!body) {
        return false;
      }

      if (body.status === undefined) {
        return true;
      }

      const status = String(body.status).toLowerCase();

      return (
        status === "success" ||
        status === "true" ||
        status === "1" ||
        body.status === true ||
        body.success === true
      );
    },

    clearCurrentResults() {
      this.historyList = [];
      this.searchedOnce = false;

      this.appliedFilters = {
        distributor_id: "",
        route_id: "",
        route_name: "",
        dealer_id: "",
        dealer_name: "",
      };
    },

    async fetchDistributors() {
      this.loadingDistributors = true;

      try {
        const response = await fetchDistributorsApi(this.profileId, this.token);
        const body = this.bodyOf(response);

        const list = this.listOf(body)
          .map((distributor) => this.mapDistributorRow(distributor))
          .filter((distributor) => distributor.profile_id);

        this.distributorList = [];
        this.selectedDistributorId = "";
        this.isDistributorLocked = false;

        if (this.isDistributorUser && this.profileId) {
          const mine = list.find((distributor) => {
            return Number(distributor.profile_id) === Number(this.profileId);
          });

          if (mine) {
            this.distributorList = [mine];
            this.selectedDistributorId = String(mine.profile_id);
            this.isDistributorLocked = true;

            await this.loadDeliveryRoutes();

            return;
          }
        }

        this.distributorList = list;
      } catch (error) {
        console.error("Error fetching distributors:", error);
        console.error("Distributor API response:", error.response);

        this.distributorList = [];
        this.selectedDistributorId = "";

        this.toast.error("Failed to load distributors.");
      } finally {
        this.loadingDistributors = false;
      }
    },

    mapDistributorRow(distributor) {
      const profileId =
        distributor.profile_id ||
        distributor.profileId ||
        distributor.ProfileId ||
        distributor.id ||
        distributor.distributor_id ||
        "";

      const name =
        distributor.name ||
        distributor.distributor_name ||
        distributor.business_name ||
        distributor.business_display_name ||
        distributor.profile_name ||
        `Distributor #${profileId}`;

      return {
        ...distributor,
        profile_id: String(profileId || ""),
        id: String(profileId || ""),
        name,
      };
    },

    async loadDeliveryRoutes() {
      if (!this.selectedDistributorId) {
        this.routeList = [];
        this.selectedRouteId = "";
        return;
      }

      this.loadingRoutes = true;

      try {
        const response = await fetchRouteList(
          this.selectedDistributorId,
          this.token,
        );

        const body = this.bodyOf(response);

        this.routeList = this.listOf(body)
          .map((route) => this.mapRouteRow(route))
          .filter((route) => route.id);

        if (!this.routeList.length) {
          this.toast.error(
            "No delivery routes found for selected distributor.",
          );
        }
      } catch (error) {
        console.error("Delivery route load error:", error);
        console.error("Route API response:", error.response);

        this.routeList = [];
        this.selectedRouteId = "";

        this.toast.error("Failed to load delivery routes.");
      } finally {
        this.loadingRoutes = false;
      }
    },

    mapRouteRow(route) {
      const routeId =
        route.id ||
        route.route_id ||
        route.routeId ||
        route.delivery_route_id ||
        "";

      const routeName =
        route.route_name ||
        route.name ||
        route.delivery_route_name ||
        `Route #${routeId}`;

      return {
        ...route,
        id: String(routeId || ""),
        distributor_id: String(this.selectedDistributorId || ""),
        name: routeName,
      };
    },

    async loadDealerFilterList() {
      this.dealerList = [];
      this.selectedDealerId = "";

      if (!this.selectedRouteId) {
        return;
      }

      this.loadingDealers = true;

      try {
        const dateRange = this.getDealerFilterDateRange();

        const payload = {
          route_ids: [Number(this.selectedRouteId)],
          start_date: dateRange.start_date,
          end_date: dateRange.end_date,
        };

        const response = await fetchDealersByRoutesHistory(payload, this.token);
        const body = this.bodyOf(response);

        if (!this.isSuccessResponse(body)) {
          this.toast.error(
            body && body.message ? body.message : "Failed to load dealers.",
          );
          return;
        }

        const extracted = this.extractDealerAndVehicleLists(body);

        this.dealerList = extracted.dealers
          .map((dealer, index) => this.mapDealerFilterRow(dealer, index))
          .filter((dealer) => dealer.id);

        if (!this.dealerList.length) {
          this.toast.error("No dealers found for selected route.");
        }
      } catch (error) {
        console.error("Dealer filter load error:", error);
        console.error("Dealer filter API response:", error.response);

        this.dealerList = [];

        const message =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : "Failed to load dealers.";

        this.toast.error(message);
      } finally {
        this.loadingDealers = false;
      }
    },

    extractDealerAndVehicleLists(body) {
      const dealerList = this.namedListOf(body, [
        "dealers",
        "dealer_list",
        "dealerList",
        "dealer_data",
        "dealerData",
        "dealer_profile_list",
        "dealerProfileList",
      ]);

      const vehicleList = this.namedListOf(body, [
        "vehicles",
        "vehicle_list",
        "vehicleList",
        "vehicle_data",
        "vehicleData",
        "delivery_vehicles",
        "deliveryVehicles",
        "sales_reps",
        "salesRepList",
        "sales_representatives",
        "reps",
      ]);

      return {
        dealers: dealerList.length ? dealerList : this.listOf(body),
        vehicles: vehicleList,
      };
    },

    mapDealerFilterRow(dealer, index) {
      const dealerId =
        dealer.profile_id ||
        dealer.dealer_profile_id ||
        dealer.dealer_id ||
        dealer.id ||
        "";

      const name =
        dealer.business_display_name ||
        dealer.business_name ||
        dealer.dealer_name ||
        dealer.name ||
        dealer.profile_name ||
        dealer.first_name ||
        `Dealer ${index + 1}`;

      const routeName =
        dealer.route_name ||
        dealer.route ||
        this.getRouteNameById(this.selectedRouteId) ||
        "-";

      return {
        ...dealer,
        id: String(dealerId || ""),
        distributor_id: String(this.selectedDistributorId || ""),
        route_id: String(this.selectedRouteId || ""),
        route_name: routeName,
        name,
      };
    },

    getDealerFilterDateRange() {
      const start = new Date();
      const end = new Date();

      start.setFullYear(start.getFullYear() - 1);
      end.setFullYear(end.getFullYear() + 2);

      return {
        start_date: this.formatInputDate(start),
        end_date: this.formatInputDate(end),
      };
    },

    formatInputDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");

      return `${year}-${month}-${day}`;
    },

    async loadDeliveryScheduleHistory(showToast = true) {
      this.loading = true;

      try {
        const response = await fetchDeliverySchedules(this.token);
        const body = this.bodyOf(response);

        if (!this.isSuccessResponse(body)) {
          this.historyList = [];

          this.toast.error(
            body && body.message
              ? body.message
              : "Failed to load delivery schedule history.",
          );

          return;
        }

        this.historyList = this.listOf(body)
          .map((schedule) => this.mapScheduleRow(schedule))
          .filter((schedule) => schedule.id);

        if (showToast) {
          if (this.filteredHistoryList.length) {
            this.toast.success("Delivery schedule history loaded.");
          } else {
            this.toast.error("No delivery schedule history found.");
          }
        }
      } catch (error) {
        console.error("Delivery schedule history search error:", error);
        console.error(
          "Delivery schedule history API response:",
          error.response,
        );

        this.historyList = [];

        const message =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : "Failed to load delivery schedule history.";

        this.toast.error(message);
      } finally {
        this.loading = false;
      }
    },

    mapScheduleRow(row) {
      const routeNames = this.parseCsvList(row.routes);
      const routeIds = this.parseCsvList(
        row.route_ids || row.delivery_route_ids || "",
      );

      const dealerIds = this.parseCsvList(
        row.dealer_ids || row.dealer_profile_ids || "",
      );

      const dealerNames = this.parseCsvList(
        row.dealers || row.dealer_names || "",
      );

      const active =
        row.is_active === true || row.is_active === 1 || row.is_active === "1";

      const recurrenceType = String(row.recurrence_type || "").toLowerCase();

      const scheduleDisplayId =
        row.name ||
        row.schedule_name ||
        `DS_${row.distributor_profile_id || "0"}_${String(
          row.id || "",
        ).padStart(6, "0")}`;

      return {
        ...row,

        id: String(row.id || ""),
        name: row.name || "",
        schedule_display_id: scheduleDisplayId,

        distributor_id: String(row.distributor_profile_id || ""),
        distributor_name: row.distributor_name || "-",

        route_id: routeIds.length ? routeIds[0] : "",
        route_ids: routeIds.map((id) => String(id)),
        route_name: row.routes || "-",
        route_names: routeNames,

        dealer_ids: dealerIds.map((id) => String(id)),
        dealer_names: dealerNames,

        schedule_type: recurrenceType,
        schedule_type_label: this.getFrequencyLabel(recurrenceType),
        schedule_text: this.buildScheduleText(row),
        time_slot_label: row.time_slot || "-",

        created_at: row.created_at || "",

        status: active ? "active" : "inactive",
      };
    },

    onDistributorChange() {
      this.clearCurrentResults();

      this.selectedRouteId = "";
      this.selectedDealerId = "";
      this.routeList = [];
      this.dealerList = [];

      if (this.selectedDistributorId) {
        this.loadDeliveryRoutes();
      }
    },

    onRouteChange() {
      this.clearCurrentResults();

      this.selectedDealerId = "";
      this.dealerList = [];

      if (this.selectedRouteId) {
        this.loadDealerFilterList();
      }
    },

    onDealerChange() {
      this.clearCurrentResults();
    },

    async searchHistory() {
  const selectedRouteName = this.getRouteNameById(
    this.selectedRouteId,
  );

  const selectedDealerName = this.getDealerNameById(
    this.selectedDealerId,
  );

  /*
   * Clear previous table immediately before loading
   * the newly selected filter results.
   */
  this.historyList = [];

  this.appliedFilters = {
    distributor_id: this.selectedDistributorId,
    route_id: this.selectedRouteId,
    route_name: selectedRouteName,
    dealer_id: this.selectedDealerId,
    dealer_name: selectedDealerName,
  };

  this.searchedOnce = true;

  await this.loadDeliveryScheduleHistory(true);
},

    async resetFilters() {
      if (this.isDistributorLocked && this.distributorList.length) {
        this.selectedDistributorId = String(this.distributorList[0].profile_id);
        this.selectedRouteId = "";
        this.selectedDealerId = "";
        this.dealerList = [];

        this.appliedFilters = {
          distributor_id: this.selectedDistributorId,
          route_id: "",
          route_name: "",
          dealer_id: "",
          dealer_name: "",
        };

        await this.loadDeliveryRoutes();
      } else {
        this.selectedDistributorId = "";
        this.selectedRouteId = "";
        this.selectedDealerId = "";
        this.routeList = [];
        this.dealerList = [];

        this.appliedFilters = {
          distributor_id: "",
          route_id: "",
          route_name: "",
          dealer_id: "",
          dealer_name: "",
        };
      }

      this.searchedOnce = false;
    },

    editSchedule(schedule) {
      this.$router.push({
        name: "DeliveryScheduleEdit",
        params: {
          id: schedule.id,
        },
      });
    },

    scheduleHasRoute(schedule, routeId, routeName) {
      if (!routeId && !routeName) {
        return true;
      }

      const scheduleRouteIds = Array.isArray(schedule.route_ids)
        ? schedule.route_ids.map((id) => String(id))
        : [];

      if (routeId && scheduleRouteIds.includes(String(routeId))) {
        return true;
      }

      const selectedRouteName = String(routeName || "")
        .trim()
        .toLowerCase();

      if (!selectedRouteName) {
        return false;
      }

      const scheduleRouteNames = Array.isArray(schedule.route_names)
        ? schedule.route_names.map((name) =>
            String(name || "")
              .trim()
              .toLowerCase(),
          )
        : [];

      return scheduleRouteNames.includes(selectedRouteName);
    },

    scheduleHasDealer(schedule, dealerId, dealerName) {
      if (!dealerId && !dealerName) {
        return true;
      }

      const scheduleDealerIds = Array.isArray(schedule.dealer_ids)
        ? schedule.dealer_ids.map((id) => String(id))
        : [];

      if (dealerId && scheduleDealerIds.includes(String(dealerId))) {
        return true;
      }

      const selectedDealerName = String(dealerName || "")
        .trim()
        .toLowerCase();

      if (!selectedDealerName) {
        return false;
      }

      const scheduleDealerNames = Array.isArray(schedule.dealer_names)
        ? schedule.dealer_names.map((name) =>
            String(name || "")
              .trim()
              .toLowerCase(),
          )
        : [];

      return scheduleDealerNames.includes(selectedDealerName);
    },

    getRouteNameById(routeId) {
      if (!routeId) {
        return "";
      }

      const route = this.routeList.find((item) => {
        return String(item.id) === String(routeId);
      });

      return route && route.name ? route.name : "";
    },

    getDealerNameById(dealerId) {
      if (!dealerId) {
        return "";
      }

      const dealer = this.dealerList.find((item) => {
        return String(item.id) === String(dealerId);
      });

      return dealer && dealer.name ? dealer.name : "";
    },

    parseCsvList(value) {
      if (Array.isArray(value)) {
        return value.map((item) => String(item).trim()).filter((item) => item);
      }

      if (value === null || value === undefined || value === "") {
        return [];
      }

      return String(value)
        .split(",")
        .map((item) => item.trim())
        .filter((item) => item);
    },

    buildScheduleText(row) {
      const recurrenceType = String(row.recurrence_type || "").toLowerCase();

      const monthText = this.parseCsvList(row.months)
        .map((month) => this.getMonthLabel(month))
        .join(", ");

      const weekText = this.parseCsvList(row.weeks)
        .map((week) => this.getWeekLabel(week))
        .join(", ");

      const weekdayText = this.parseCsvList(row.weekdays)
        .map((day) => this.getDayLabel(day))
        .join(", ");

      const parts = [];

      if (recurrenceType === "month" || recurrenceType === "month_by_month") {
        if (monthText) {
          parts.push(monthText);
        }

        if (weekText) {
          parts.push(weekText);
        }

        if (weekdayText) {
          parts.push(weekdayText);
        }
      } else if (
        recurrenceType === "week" ||
        recurrenceType === "week_by_week"
      ) {
        if (weekText) {
          parts.push(weekText);
        }

        if (weekdayText) {
          parts.push(weekdayText);
        }
      } else {
        if (weekdayText) {
          parts.push(weekdayText);
        }
      }

      return parts.length ? parts.join(" • ") : "-";
    },

    getDayLabel(value) {
      const cleanValue = String(value || "")
        .trim()
        .toLowerCase();

      const dayMap = {
        1: "Mon",
        2: "Tue",
        3: "Wed",
        4: "Thu",
        5: "Fri",
        6: "Sat",
        7: "Sun",
        monday: "Mon",
        mon: "Mon",
        tuesday: "Tue",
        tue: "Tue",
        wednesday: "Wed",
        wed: "Wed",
        thursday: "Thu",
        thu: "Thu",
        friday: "Fri",
        fri: "Fri",
        saturday: "Sat",
        sat: "Sat",
        sunday: "Sun",
        sun: "Sun",
      };

      return dayMap[cleanValue] || value;
    },

    getWeekLabel(value) {
      const cleanValue = String(value || "").trim();

      if (!cleanValue) {
        return "";
      }

      if (cleanValue.toLowerCase().startsWith("week")) {
        return cleanValue;
      }

      return `Week ${cleanValue}`;
    },

    getMonthLabel(value) {
      const cleanValue = String(value || "")
        .trim()
        .toLowerCase();

      const monthMap = {
        1: "Jan",
        2: "Feb",
        3: "Mar",
        4: "Apr",
        5: "May",
        6: "Jun",
        7: "Jul",
        8: "Aug",
        9: "Sep",
        10: "Oct",
        11: "Nov",
        12: "Dec",
        january: "Jan",
        february: "Feb",
        march: "Mar",
        april: "Apr",
        may: "May",
        june: "Jun",
        july: "Jul",
        august: "Aug",
        september: "Sep",
        october: "Oct",
        november: "Nov",
        december: "Dec",
      };

      return monthMap[cleanValue] || value;
    },

    formatDate(value) {
      if (!value) {
        return "-";
      }

      const date = this.toDate(value);

      if (!date) {
        return value;
      }

      return date.toLocaleDateString("en-GB", {
        year: "numeric",
        month: "short",
        day: "2-digit",
      });
    },

    formatTime(value) {
      if (!value) {
        return "";
      }

      const date = this.toDate(value);

      if (!date) {
        return "";
      }

      return date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    toDate(value) {
      const normalizedValue = String(value).replace(" ", "T");
      const date = new Date(normalizedValue);

      if (Number.isNaN(date.getTime())) {
        return null;
      }

      return date;
    },

    getFrequencyLabel(type) {
      const normalizedType = String(type || "").toLowerCase();

      if (normalizedType === "day" || normalizedType === "day_by_day") {
        return "Day by Day";
      }

      if (normalizedType === "week" || normalizedType === "week_by_week") {
        return "Week By Week";
      }

      if (normalizedType === "month" || normalizedType === "month_by_month") {
        return "Month By Month";
      }

      return "-";
    },

    getStatusLabel(status) {
      if (status === "active") {
        return "Active";
      }

      if (status === "inactive") {
        return "Inactive";
      }

      if (status === "draft") {
        return "Draft";
      }

      return status || "-";
    },

    getStatusClass(status) {
      if (status === "active") {
        return "active";
      }

      if (status === "inactive") {
        return "inactive";
      }

      if (status === "draft") {
        return "draft";
      }

      return "";
    },
  },
};
</script>

<style scoped>
.delivery-history-page {
  padding-bottom: 24px;
}

.history-container {
  width: calc(100% - 20px);
  margin: 10px;
  padding: 16px;
  box-sizing: border-box;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03),
    0 4px 18px rgba(15, 23, 42, 0.06);
}

/* =========================================================
   SECTION
========================================================= */

.section-card {
  position: relative;
  margin-bottom: 13px;
  padding: 13px;
  border: 1px solid #e5e7eb;
  border-radius: 7px;
  background: #ffffff;
}

.section-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 11px;
}

.section-header h6 {
  margin: 0;
  color: #111827;
  font-size: 13.5px;
  font-weight: 800;
}

.section-header span {
  display: block;
  margin-top: 2px;
  color: #6b7280;
  font-size: 11.5px;
}

.section-count,
.table-summary-pill {
  flex-shrink: 0;
  padding: 4px 8px;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  background: #f3f4f6;
  color: #374151;
  font-size: 10.5px;
  font-weight: 800;
}

.loading-card {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  min-height: 125px;
  border: 1px dashed #d1d5db;
  border-radius: 7px;
  background: #fafafa;
  color: #6b7280;
  font-size: 12px;
  font-weight: 800;
}

/* =========================================================
   FILTERS
========================================================= */

.filter-grid {
  display: grid;
  grid-template-columns:
    minmax(200px, 1fr)
    minmax(200px, 1fr)
    minmax(220px, 1.2fr)
    95px
    90px;
  gap: 10px;
  align-items: end;
}

.form-item {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.form-item label {
  display: block;
  margin-bottom: 4px;
  color: #374151;
  font-size: 11.5px;
  font-weight: 800;
}

.form-item input,
.form-item select {
  width: 100%;
  height: 34px;
  min-height: 34px;
  padding: 6px 9px;
  box-sizing: border-box;
  border: 1px solid #d1d5db;
  border-radius: 5px;
  outline: none;
  background: #ffffff;
  color: #111827;
  font-size: 12.5px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease,
    background 0.15s ease;
}

.form-item input:focus,
.form-item select:focus {
  border-color: #9ca3af;
  box-shadow: 0 0 0 2px rgba(107, 114, 128, 0.1);
}

.form-item input:disabled,
.form-item select:disabled {
  background: #f3f4f6;
  color: #6b7280;
  cursor: not-allowed;
}

.hidden-label {
  visibility: hidden;
}

.filter-note {
  margin-top: 9px;
  padding: 7px 9px;
  border: 1px solid #fed7aa;
  border-radius: 6px;
  background: #fff7ed;
  color: #9a3412;
  font-size: 10.5px;
  font-weight: 800;
}

.btn {
  height: 34px;
  min-height: 34px;
  padding: 6px 13px;
  border: none;
  border-radius: 5px;
  color: #ffffff;
  font-size: 12.5px;
  font-weight: 800;
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 0.3s, opacity 0.15s ease, transform 0.15s ease,
    box-shadow 0.15s ease;
}

.btn:hover:not(:disabled) {
  transform: translateY(-1px);
  opacity: 0.96;
}

.btn:disabled {
  background: #6c757d !important;
  cursor: not-allowed;
  opacity: 0.7;
  transform: none;
}

.delivery-history-page .btn.button-default.search-button {
  width: 100%;
  background-color: #007bff !important;
  color: #ffffff !important;
}

.delivery-history-page .btn.button-default.search-button:hover:not(:disabled) {
  background-color: #0056b3 !important;
  color: #ffffff !important;
}

.delivery-history-page .btn.button-default.search-button:active:not(:disabled) {
  transform: scale(0.98);
}

.delivery-history-page .btn.button-default.search-button:disabled {
  background-color: #6c757d !important;
  color: #ffffff !important;
  cursor: not-allowed;
  opacity: 0.7;
}

.reset-button {
  width: 100%;
  background: #6c757d;
}

.reset-button:hover:not(:disabled) {
  background: #5c636a;
}

/* =========================================================
   TABLE
========================================================= */

.history-table-card {
  overflow: hidden;
  border: 1px solid #e5e7eb;
  border-radius: 7px;
  background: #ffffff;
}

.history-table-scroll {
  max-height: 430px;
  overflow: auto;
  scrollbar-gutter: stable;
}

.history-table {
  width: 100%;
  min-width: 1030px;
  margin: 0;
  border-collapse: separate;
  border-spacing: 0;
}

.history-table th,
.history-table td {
  padding: 9px 10px;
  border-right: 1px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;
  background: #ffffff;
  color: #111827;
  font-size: 12.5px;
  vertical-align: middle;
  white-space: nowrap;
}

.history-table thead th {
  position: sticky;
  top: 0;
  z-index: 4;
  background: #f9fafb;
  color: #374151;
  font-weight: 900;
}

.history-table tbody tr:hover td {
  background: #f8fafc;
}

.primary-cell {
  color: #111827;
  font-weight: 900;
}

.schedule-cell,
.date-cell {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.schedule-cell strong,
.date-cell strong {
  color: #111827;
  font-size: 12px;
  font-weight: 900;
}

.schedule-cell span,
.date-cell span {
  margin-top: 2px;
  color: #6b7280;
  font-size: 10.5px;
  font-weight: 700;
}

.schedule-cell small {
  margin-top: 2px;
  color: #374151;
  font-size: 10px;
  font-weight: 800;
}

.schedule-id-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 108px;
  width: fit-content;
  padding: 4px 8px;
  border-radius: 999px;
  background: #f3f4f6;
  color: #111827;
  font-size: 10.5px;
  font-weight: 900;
  letter-spacing: 0.02em;
}

.route-pill {
  display: inline-flex;
  align-items: center;
  max-width: 260px;
  overflow: hidden;
  padding: 4px 8px;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  background: #f3f4f6;
  color: #374151;
  font-size: 10.5px;
  font-weight: 900;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.status-pill {
  display: inline-block;
  min-width: 66px;
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 900;
  text-align: center;
}

.status-pill.active {
  background: #dcfce7;
  color: #166534;
}

.status-pill.inactive {
  background: #fee2e2;
  color: #991b1b;
}

.status-pill.draft {
  background: #fef3c7;
  color: #92400e;
}

.action-column {
  width: 105px;
  text-align: center;
}

.edit-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  min-width: 74px;
  height: 29px;
  padding: 5px 9px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #f9fafb;
  color: #374151;
  font-size: 10.5px;
  font-weight: 900;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease,
    transform 0.15s ease;
}

.edit-button:hover {
  border-color: #9ca3af;
  background: #f3f4f6;
  transform: translateY(-1px);
}

.empty-table {
  padding: 26px 10px !important;
  color: #6b7280 !important;
  text-align: center;
}

/* =========================================================
   PREMIUM TINY SCROLLBARS
========================================================= */

.delivery-history-page,
.delivery-history-page * {
  scrollbar-width: thin;
  scrollbar-color: rgba(100, 116, 139, 0.34) transparent;
}

.delivery-history-page *::-webkit-scrollbar {
  width: 5px;
  height: 5px;
}

.delivery-history-page *::-webkit-scrollbar-track {
  background: transparent;
}

.delivery-history-page *::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: rgba(100, 116, 139, 0.27);
}

.delivery-history-page *::-webkit-scrollbar-thumb:hover {
  background: rgba(100, 116, 139, 0.55);
}

.delivery-history-page *::-webkit-scrollbar-corner {
  background: transparent;
}

/* =========================================================
   RESPONSIVE
========================================================= */

@media (max-width: 1280px) {
  .filter-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .button-item {
    min-width: 90px;
  }
}

@media (max-width: 850px) {
  .history-container {
    width: calc(100% - 12px);
    margin: 6px;
    padding: 12px;
  }

  .filter-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .section-header {
    align-items: flex-start;
  }
}

@media (max-width: 520px) {
  .filter-grid {
    grid-template-columns: 1fr;
  }

  .section-header {
    gap: 8px;
  }

  .section-count,
  .table-summary-pill {
    font-size: 10px;
  }

  .history-table-scroll {
    max-height: 390px;
  }
}
</style>
